import { TImageAsset, TProject, TVideoAsset, TVimeoVideoMetadata } from '@/types/global';
import { Button } from '@/components/atoms/Button/Button';
import { Image } from '@/components/atoms/Image/Image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '@/components/atoms/Typography/Typography';
import { events } from '@/utils/events';
import { allowPageScroll, blockPageScroll } from '@/utils/storeSetters';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { useTranslations } from 'next-intl';
import { useSwipeable } from 'react-swipeable';
import { useVisibilityFromBreadcrumb } from '@/hooks/useVisibilityFromBreadcrumb';
import { usePageStore } from '@/store/usePageStore';
import { useFiltersStore } from '@/store/useFiltersStore';
import * as Styled from './GallerySliderModal.styled';
import { usePopFromPrefiltredProj } from '@/hooks/usePopFromPrefiltredProj';
import { VimeoVideo } from '@/components/atoms/VimeoVideo/VimeoVideo';


export interface IGallerySliderModalProps {
  title: string;
  medias: (TImageAsset | TVideoAsset)[];
  nextPrevious?: {
    next?: TProject | null;
    previous?: TProject | null;
  };
  assetsWithProjectsData?: {
    projectSlug: string;
    projectTitle: string;
    imageSrc: string;
  }[];
  playsVideosInline?: boolean;
}

export const GallerySliderModal = ({
  title,
  medias,
  nextPrevious,
  assetsWithProjectsData = [], 
  playsVideosInline = true,
}: IGallerySliderModalProps) => {
  const isDisplayedFromBreadcrumb = useVisibilityFromBreadcrumb();
  const [displayedImgIndex, setDisplayedImgIndex] = useState(0);
  const [displayNextProj, setDisplayNextProj] = useState(false);
  const [displayPreviousProj, setDisplayPrevProj] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [hideRightNav, setHideRightNav] = useState(false);
  const [hideLeftNav, setHideLeftNav] = useState(false);
  const [percentageScroll, setPercentageScroll] = useState(0);
  const refModal = useRef<HTMLDivElement>(null);
  const stateSelector = usePageStore;
  const isImagesGalleryDisplayed = stateSelector((s) => s.isImagesGalleryDisplayed);
  const setIsImagesGalleryDisplayed = stateSelector(
    (s) => s.setIsImagesGalleryDisplayed
  );
  const isPresentationMode = usePageStore(
    (s) => s.isPresentationMode
  );

  const nextPreviousFromSearch = useFiltersStore(
    (s) => s.currentProjectNextPrevious
  );

  const refIsImageZoomed = useRef(false);

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (startX === null) {
        setStartX(eventData.initial[0]);
        return;
      }
  
      const currentX =
        'touches' in eventData.event
          ? eventData.event.touches[0]?.clientX
          : eventData.event.clientX;
  
      const percentageScroll =
        ((currentX - startX) / window.innerWidth) * 1;
  
      setPercentageScroll(Math.max(-0.2, Math.min(0.2, percentageScroll)));
    },
    onSwipedLeft: () => displayNextImg(),
    onSwipedRight: () => {
      displayPreviousImg()
    },
    onSwiped: () => {
      setStartX(null);
      setPercentageScroll(0);
    },
    trackTouch: true,
    trackMouse: true,
    delta: 10,
    preventScrollOnSwipe: true,
  });

  const translations = useTranslations('project');
  const t = translations.raw('nextPrevious') as {
    prev: string;
    next: string;
  };

  const openModalFromImgClick = useCallback(
    (e: Event) => {
      // Request fullscreen
      if (document.documentElement.requestFullscreen && !isImagesGalleryDisplayed) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error('Failed to enter fullscreen:', err);
        });
      }
      setIsImagesGalleryDisplayed(true);
      const imgSrc = (e as CustomEvent).detail;

      const imgIndex = medias.findIndex((media) => {
        if ((media as TVideoAsset).vimeoId) {
          return (media as TVideoAsset).vimeoId === imgSrc || media.src === imgSrc;
        }
        return media.src === imgSrc;
      });
      setDisplayedImgIndex(imgIndex);
      setIsImagesGalleryDisplayed(!isImagesGalleryDisplayed);
    },
    [medias, isImagesGalleryDisplayed, setIsImagesGalleryDisplayed]
  );

  useEffect(() => {
    window.addEventListener(
      events.toggleGallerySliderModal,
      openModalFromImgClick
    );
    return () => {
      window.removeEventListener(
        events.toggleGallerySliderModal,
        openModalFromImgClick
      );
    };
  }, [openModalFromImgClick]);

  useEffect(() => {
    setDisplayPrevProj(false);
    setDisplayNextProj(false);
  }, [displayedImgIndex]);

  useEffect(() => {
    if (isImagesGalleryDisplayed) {
      blockPageScroll();
    } else {
      allowPageScroll();
    }
  }, [isImagesGalleryDisplayed]);

  const closeModal = () => {
    setIsImagesGalleryDisplayed(false);

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen:', err);
      });
    }
  };

  const isPreviousPopFromRelated = usePopFromPrefiltredProj();

  const displayNextImg = useCallback(() => {
    // next project shows when we come from work or presentation and there is a next project in the search
    const isNextProjectInGallery = (isDisplayedFromBreadcrumb || isPresentationMode) && !!nextPreviousFromSearch?.next && !isPreviousPopFromRelated;

    setDisplayedImgIndex((prev) => {
      if (displayPreviousProj) {
        setDisplayPrevProj(false);
        return prev;
      }
  
      if (prev < medias.length - 1) {
        setDisplayPrevProj(false);
        setHideRightNav(false);
        setHideLeftNav(false);

        if ((medias.length === displayedImgIndex + 2) && !isNextProjectInGallery && isPresentationMode && isDisplayedFromBreadcrumb) {
          setHideRightNav(true);
          setDisplayNextProj(false);
          return prev;
        } else if (nextPrevious?.next) {
          setHideRightNav(false);
        }
        return prev + 1;
      }
  
      // if we reach the end of the gallery, show next project if necessary
      if (isNextProjectInGallery || nextPrevious?.next) {
        setDisplayNextProj(true);
      } 
  
      return prev; // fallback if no changes
    });
  }, [isPreviousPopFromRelated, displayPreviousProj, medias.length, isDisplayedFromBreadcrumb, displayedImgIndex, isPresentationMode, nextPreviousFromSearch?.next, nextPrevious?.next]);

  const displayPreviousImg = useCallback(() => {
    // previous project shows when we come from work or presentation and there is a previous project in the search
    const isPreviousProjectInGallery = (isDisplayedFromBreadcrumb || isPresentationMode) && (nextPreviousFromSearch?.previous);

    setDisplayedImgIndex((prev) => {
      if (prev > 0) {
        setDisplayNextProj(false);
        setHideRightNav(false);
        setHideLeftNav(false);

        if (prev === 1 && !isPreviousProjectInGallery && isPresentationMode && isDisplayedFromBreadcrumb) {
          setHideLeftNav(true);
        } else if (nextPrevious?.previous) {
          setHideLeftNav(false);
        }
        return prev - 1;
      }

      if (isPreviousProjectInGallery || nextPrevious?.previous) {
        setDisplayPrevProj(true);
      } 

      return prev;
    });
  }, [isDisplayedFromBreadcrumb, nextPreviousFromSearch?.previous, nextPrevious?.previous, isPresentationMode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Tab' || e.key === 'Enter') {
        displayNextImg();
      } else if (e.key === 'ArrowLeft') {
        displayPreviousImg();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [displayNextImg, displayPreviousImg]);

  useEffect(() => {
    let isThrottled = false;

    refIsImageZoomed.current = false;

    const handleWheel = (e: WheelEvent) => {
      if (!isImagesGalleryDisplayed || isThrottled) return;
  
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 30) {
        isThrottled = true;
  
        if (e.deltaX > 0) {
          displayNextImg();
        } else {
          displayPreviousImg();
        }
  
        // lock for 500ms (adjustable)
        setTimeout(() => {
          isThrottled = false;
        }, 500);
      }
    };
  
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [isImagesGalleryDisplayed, displayNextImg, displayPreviousImg]);


  const renderNavItem = (
    item: TProject,
    side: 'left' | 'right',
    label: string
  ) => {
    if (!item) return null;

    return (
      <Styled.NextPrevContainer
        data-side={side}
        data-display={side === 'right' ? displayNextProj : displayPreviousProj}
      >
        <CustomLink
          data-slug={JSON.stringify(item.slug)}
          href={`${item.slug}?prev=gallery`}
          className="prev"
        >
          <Typography text={label} color="secondary" size="base" />
          <Image
            includeContainer={true}
            src={item.image?.src || '#'}
            alt={item.image?.alt || 'img navigate'}
            height={item.image?.height}
            width={item.image?.width}
            fill={true}
            aspectRatio="15/10"
            lazy={true}
            fadeInOnLoad={true}
          />
          <Typography text={item.title} size="lg" />
          <Typography text={item.location} color="secondary" size="base" />
        </CustomLink>
      </Styled.NextPrevContainer>
    )
  };

  return isImagesGalleryDisplayed ? (
    <Styled.GallerySliderModal
      data-is-displayed={isImagesGalleryDisplayed}
      ref={refModal}
    >
      { assetsWithProjectsData.length === 0 ? (
        <Typography text={title} className="title" />
      ) : (
        <Styled.LinksContainer className="title">
          <CustomLink
            href={assetsWithProjectsData[displayedImgIndex]?.projectSlug || ''} 
            text={assetsWithProjectsData[displayedImgIndex]?.projectTitle || ''} 
            className="title gallery-link-project" 
            target='_blank'
          />
          <CustomLink
            href={assetsWithProjectsData[displayedImgIndex]?.imageSrc || ''} 
            text={{
              en: 'edit asset',
              es: 'editar recurso',
            }} 
            className="title gallery-link-image" 
            target='_blank'
          />
        </Styled.LinksContainer>
      ) }

      
      <Styled.ImagesContainer
        {...handlers}
        data-is-end={displayNextProj || displayPreviousProj}
      >
        <Styled.ImagesWrapper
          style={
            {
              '--percentage-scroll': `${percentageScroll || 0}`,
              '--transform-slider-mobile': `${displayedImgIndex + percentageScroll * -1}`,
              '--slider-num-img': `${medias.length}`,
            } as React.CSSProperties
          }
          data-img-displayed={displayedImgIndex}
        >
        {medias.map((media, i) => {
          const isVideo = (media: TImageAsset | TVideoAsset): media is TVideoAsset => 'vimeoId' in media;
          const inWindow = Math.abs(i - displayedImgIndex) <= 3;
          const SAFE_BG = 'data:image/gif;base64,R0lGODlhAQABAPAAqampqf///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==';

          if (!isVideo(media)) {
            return (
              <Image
                className="gallery-slider-img"
                data-num={displayedImgIndex}
                data-order-media={i}
                key={`img-slider-gallery-${i}`}
                lazy={true}
                {...media}
                src={inWindow ? media.src : SAFE_BG}
                alt={media.alt}
                data-display={i === displayedImgIndex ? 'true' : 'false'}
                fadeInOnLoad={false}
                aspectRatio={media.width && media.height ? `${media.width}/${media.height}` : 'unset'}
                showLoaderSpinner={i === displayedImgIndex}
                classNameContainer='gallery-slider-img-container'
                isNotDownloadable={true}
                sizes={
                  {
                    desktop: '100vw',
                    tablet: '100vw',
                    mobile: '100vw',
                  }
                }
              />
            );
          } else {
            return (
              <Styled.VideoWrapper
                data-display={i === displayedImgIndex ? 'true' : 'false'}
                data-order-media={i}
                key={`video-slider-gallery-${i}`}
                className='gallery-slider-video-container'
                style={{
                  aspectRatio: media.vimeoMetadata?.aspectRatio,
                  overflow: 'hidden',
                  height: 'auto',
                  pointerEvents: media.includesControls ? 'auto' : 'none',
                }}
              >
                <VimeoVideo
                  key={`video-${media.src}`}
                  src={media.src}
                  poster={media.poster}
                  {...(media.includesSound ? { includesSound: media.includesSound } : {})}
                  {...(media.vimeoId ? { vimeoId: media.vimeoId } : {})}
                  // includesControls={true}
                  {...{ aspectRatio: media.aspectRatio ?? '' }}
                  type={media.type || 'video/mp4'}
                  classNameContainer={'vimeo-video-container'}
                  vimeoMetadata={media.vimeoMetadata as TVimeoVideoMetadata}
                  autoplay={playsVideosInline ? "whenVisible" : "never"}
                  scaleVideoToFit={false}
                  includesControls={media.includesControls}
                />
              </Styled.VideoWrapper>
            );
          }
        })}
        </Styled.ImagesWrapper>
      </Styled.ImagesContainer>
      <Styled.ImageCounter>
        <Typography
          text={`${displayedImgIndex + 1} <span style="color:var(--c-font-secondary);"> / ${medias.length}</span>`}
          size="lg"
        />
      </Styled.ImageCounter>
      <Styled.NavListenerLeft 
        onClick={displayPreviousImg} 
        className="nav-listener-left"
        data-is-hidden={hideLeftNav}
      >
        <Styled.IconButton>
          <svg
            width="44"
            height="86"
            viewBox="0 0 44 86"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M43.5 85L1.5 43L43.5 1"
              stroke="var(--c-black-200)"
              strokeWidth={2}
            />
          </svg>
        </Styled.IconButton>
      </Styled.NavListenerLeft>
      <Styled.NavListenerRight 
        onClick={displayNextImg} 
        className="nav-listener-right"
        data-is-hidden={hideRightNav}
      >
        <Styled.IconButton> 
          <svg
            width="44"
            height="86"
            viewBox="0 0 44 86"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 1L42.5 43L0.5 85"
              stroke="var(--c-black-200)"
              strokeWidth={2}
            />
          </svg>
        </Styled.IconButton>
      </Styled.NavListenerRight>
      {nextPreviousFromSearch?.previous ? (
        renderNavItem(nextPreviousFromSearch.previous, 'left', t.prev)
      ) : nextPrevious?.previous && (
        renderNavItem(nextPrevious.previous, 'left', t.prev)
      )}
      {nextPreviousFromSearch?.next?.image ? (
        renderNavItem(nextPreviousFromSearch.next, 'right', t.next)
      ) : nextPrevious?.next && (
        renderNavItem(nextPrevious.next, 'right', t.next)
      )}
      <Button
        className={'close-btn'}
        scaleOnHover={true}
        callbackOnClick={() => closeModal()}
      >
        <Image
          src="/img/icons/cross.svg"
          alt="close medias gallery"
          height={35}
          width={35}
          fill={false}
        />
      </Button>
      <Styled.ScrollIndicatorContainer>
        <Styled.ScrollIndicator
          style={
            {
              '--scroll-percent': `${
                !displayPreviousProj
                  ? (displayedImgIndex + 1 + (displayNextProj ? 1 : 0)) / medias.length
                  : 0
              }`,
            } as React.CSSProperties
          }
        />
      </Styled.ScrollIndicatorContainer>
    </Styled.GallerySliderModal>
  ) : (
    <></>
  );
};


  // TO-DO: zoom descartado porque las imagenes no tienen suficiente resolución, a confirmar definitivamente
  // 
  // const handleMouseMove = (e: MouseEvent, image: HTMLElement) => {
  //   const container = e.currentTarget as HTMLElement;

  //   if (!container || !image || !refIsImageZoomed.current) return;

  //   const rect = container.getBoundingClientRect();
  //   const offsetX = e.clientX - rect.left;
  //   const offsetY = e.clientY - rect.top;

  //   const percentX = offsetX / rect.width;  // 0 izquierda -> 1 derecha
  //   const percentY = offsetY / rect.height;

  //   const zoomScale = 1.25;

  //   const visibleWidth = rect.width;
  //   const visibleHeight = rect.height;

  //   const fullImageWidth = visibleWidth * zoomScale;
  //   const fullImageHeight = visibleHeight * zoomScale;

  //   const maxOffsetX = (fullImageWidth - visibleWidth) / 2;
  //   const maxOffsetY = (fullImageHeight - visibleHeight) / 2;

  //   // Movimiento desde el centro (percentX 0.5 => no se mueve)
  //   const moveX = (percentX - 0.5) * 2 * maxOffsetX;
  //   const moveY = (percentY - 0.5) * 2 * maxOffsetY;

  //   // Aquí respetamos el translateX(-50%) inicial
  //   image.style.transform = `translate(calc(-50% + ${-moveX}px), ${-moveY}px) scale(${zoomScale})`;
  // };

  // const toggleCurrentImageZoom = () => {
  //   const isZoomed = refIsImageZoomed.current;

  //   if (refModal.current) {
  //     const currentMediaContainer = refModal.current?.querySelector(`div[data-img-displayed="${displayedImgIndex}"]`) as HTMLElement | null;
  //     const image: HTMLElement | null | undefined = currentMediaContainer?.querySelector(`.gallery-slider-img[data-order-media="${displayedImgIndex}"]`);

  //     if (currentMediaContainer && image) {
  //       if (!isZoomed) {
  //         image.style.transform = 'translate(-50%, 0) scale(1.25)';
  //         currentMediaContainer.style.zIndex = '5000';
  //         currentMediaContainer.style.pointerEvents = 'auto';
  //         currentMediaContainer.addEventListener('mousemove', (e) => handleMouseMove(e, image));
  //         refCurrentlyZoomedImage.current = image;
  //       } else {
  //         image.style.transform = 'translateX(-50%) scale(1)';
  //         currentMediaContainer.style.zIndex = '0';
  //         currentMediaContainer.style.pointerEvents = 'none';
  //         currentMediaContainer.removeEventListener('mousemove', (e) => handleMouseMove(e, image));
  //       }

  //       refIsImageZoomed.current = !isZoomed;
  //     }
  //   }
  // }